/**
 * Tailwind-aware merging of a component `preset` base class string with a
 * caller supplied `className` override.
 *
 * Why this exists
 * ---------------
 * Tailwind resolves conflicting utilities by *stylesheet source order*, not by
 * the order the classes appear in the `class` attribute. Naively concatenating
 * a preset with a caller override therefore produces class strings such as:
 *
 *   "fixed inset-x-0 bottom-0 z-50 px-4 ... relative z-20 px-3 ..."
 *
 * where the preset can still win at runtime (`fixed` over `relative`, a preset
 * gradient painting over an overriding background colour, and so on).
 *
 * Behaviour
 * ---------
 * - The override string is returned untouched and always last.
 * - A base (preset) class is dropped only when the override contains a class
 *   with the *same variant set* whose conflict group is the same as, or
 *   broader than, the base class group (`px-3` clears `px-4`; `p-2` clears
 *   every padding side; `bg-white` clears preset gradient stops).
 * - Unrecognised utilities have no group and are always kept, so unknown or
 *   project specific classes degrade to plain concatenation.
 *
 * This is deliberately narrower than `tailwind-merge`: it never reorders or
 * de-duplicates the caller's own classes, which keeps the blast radius of the
 * change limited to preset vs. override conflicts.
 */

interface ParsedToken {
  token: string;
  variantKey: string;
  group: string | null;
}

/** Utilities whose group can be resolved from the exact class name. */
const EXACT_GROUPS: Readonly<Record<string, string>> = {
  static: 'position',
  fixed: 'position',
  absolute: 'position',
  relative: 'position',
  sticky: 'position',

  block: 'display',
  'inline-block': 'display',
  inline: 'display',
  flex: 'display',
  'inline-flex': 'display',
  table: 'display',
  'inline-table': 'display',
  'flow-root': 'display',
  grid: 'display',
  'inline-grid': 'display',
  contents: 'display',
  'list-item': 'display',
  hidden: 'display',

  visible: 'visibility',
  invisible: 'visibility',
  collapse: 'visibility',

  isolate: 'isolation',
  'isolation-auto': 'isolation',

  italic: 'font-style',
  'not-italic': 'font-style',

  underline: 'text-decoration',
  overline: 'text-decoration',
  'line-through': 'text-decoration',
  'no-underline': 'text-decoration',

  uppercase: 'text-transform',
  lowercase: 'text-transform',
  capitalize: 'text-transform',
  'normal-case': 'text-transform',

  truncate: 'text-overflow',

  border: 'border-w',
  rounded: 'rounded',
  shadow: 'shadow',
  blur: 'blur',
  'backdrop-blur': 'backdrop-blur',
  transition: 'transition',

  grow: 'flex-grow',
  shrink: 'flex-shrink',

  'sr-only': 'screen-reader',
  'not-sr-only': 'screen-reader',
};

/**
 * Prefix based groups. Order matters: longer / more specific prefixes must be
 * listed before the shorter ones they start with.
 */
const PREFIX_GROUPS: ReadonlyArray<readonly [string, string]> = [
  ['inset-x-', 'inset-x'],
  ['inset-y-', 'inset-y'],
  ['inset-', 'inset'],
  ['top-', 'top'],
  ['right-', 'right'],
  ['bottom-', 'bottom'],
  ['left-', 'left'],
  ['start-', 'inset-s'],
  ['end-', 'inset-e'],

  ['z-', 'z'],
  ['order-', 'order'],

  ['px-', 'px'],
  ['py-', 'py'],
  ['pt-', 'pt'],
  ['pr-', 'pr'],
  ['pb-', 'pb'],
  ['pl-', 'pl'],
  ['ps-', 'ps'],
  ['pe-', 'pe'],
  ['p-', 'p'],

  ['mx-', 'mx'],
  ['my-', 'my'],
  ['mt-', 'mt'],
  ['mr-', 'mr'],
  ['mb-', 'mb'],
  ['ml-', 'ml'],
  ['ms-', 'ms'],
  ['me-', 'me'],
  ['m-', 'm'],

  ['space-x-', 'space-x'],
  ['space-y-', 'space-y'],

  ['min-w-', 'min-w'],
  ['max-w-', 'max-w'],
  ['w-', 'w'],
  ['min-h-', 'min-h'],
  ['max-h-', 'max-h'],
  ['h-', 'h'],

  ['gap-x-', 'gap-x'],
  ['gap-y-', 'gap-y'],
  ['gap-', 'gap'],

  ['overflow-x-', 'overflow-x'],
  ['overflow-y-', 'overflow-y'],
  ['overflow-', 'overflow'],

  ['opacity-', 'opacity'],
  ['leading-', 'leading'],
  ['tracking-', 'tracking'],

  ['justify-items-', 'justify-items'],
  ['justify-self-', 'justify-self'],
  ['justify-', 'justify-content'],
  ['items-', 'align-items'],
  ['self-', 'align-self'],
  ['content-', 'align-content'],

  ['grid-cols-', 'grid-cols'],
  ['grid-rows-', 'grid-rows'],
  ['col-span-', 'col-span'],
  ['row-span-', 'row-span'],

  ['basis-', 'flex-basis'],
  ['grow-', 'flex-grow'],
  ['shrink-', 'flex-shrink'],

  ['backdrop-blur-', 'backdrop-blur'],
  ['blur-', 'blur'],

  ['transition-', 'transition'],
  ['duration-', 'duration'],
  ['delay-', 'delay'],
  ['ease-', 'ease'],

  ['object-', 'object'],
  ['cursor-', 'cursor'],
  ['whitespace-', 'whitespace'],
  ['align-', 'vertical-align'],
];

/**
 * Groups that fully contain other groups. A base class is removed when the
 * override group equals its group, or lists its group here.
 */
const GROUP_MEMBERS: Readonly<Record<string, readonly string[]>> = {
  p: ['px', 'py', 'pt', 'pr', 'pb', 'pl', 'ps', 'pe'],
  px: ['pl', 'pr', 'ps', 'pe'],
  py: ['pt', 'pb'],

  m: ['mx', 'my', 'mt', 'mr', 'mb', 'ml', 'ms', 'me'],
  mx: ['ml', 'mr', 'ms', 'me'],
  my: ['mt', 'mb'],

  inset: ['inset-x', 'inset-y', 'top', 'right', 'bottom', 'left', 'inset-s', 'inset-e'],
  'inset-x': ['left', 'right', 'inset-s', 'inset-e'],
  'inset-y': ['top', 'bottom'],

  gap: ['gap-x', 'gap-y'],
  overflow: ['overflow-x', 'overflow-y'],
  space: ['space-x', 'space-y'],

  rounded: [
    'rounded-t',
    'rounded-r',
    'rounded-b',
    'rounded-l',
    'rounded-s',
    'rounded-e',
    'rounded-tl',
    'rounded-tr',
    'rounded-br',
    'rounded-bl',
    'rounded-ss',
    'rounded-se',
    'rounded-ee',
    'rounded-es',
  ],
  'rounded-t': ['rounded-tl', 'rounded-tr'],
  'rounded-r': ['rounded-tr', 'rounded-br'],
  'rounded-b': ['rounded-br', 'rounded-bl'],
  'rounded-l': ['rounded-tl', 'rounded-bl'],

  'border-w': [
    'border-w-x',
    'border-w-y',
    'border-w-t',
    'border-w-r',
    'border-w-b',
    'border-w-l',
    'border-w-s',
    'border-w-e',
  ],
  'border-w-x': ['border-w-l', 'border-w-r', 'border-w-s', 'border-w-e'],
  'border-w-y': ['border-w-t', 'border-w-b'],

  'border-color': [
    'border-color-x',
    'border-color-y',
    'border-color-t',
    'border-color-r',
    'border-color-b',
    'border-color-l',
    'border-color-s',
    'border-color-e',
  ],
  'border-color-x': ['border-color-l', 'border-color-r', 'border-color-s', 'border-color-e'],
  'border-color-y': ['border-color-t', 'border-color-b'],

  // A background colour override also clears a preset gradient, otherwise the
  // preset gradient keeps painting on top of the overriding colour.
  'bg-color': ['bg-image', 'gradient-from', 'gradient-via', 'gradient-to'],
};

const FONT_SIZES = new Set([
  'xs',
  'sm',
  'base',
  'lg',
  'xl',
  '2xl',
  '3xl',
  '4xl',
  '5xl',
  '6xl',
  '7xl',
  '8xl',
  '9xl',
]);
const FONT_WEIGHTS = new Set([
  'thin',
  'extralight',
  'light',
  'normal',
  'medium',
  'semibold',
  'bold',
  'extrabold',
  'black',
]);
const TEXT_ALIGN = new Set(['left', 'center', 'right', 'justify', 'start', 'end']);
const TEXT_WRAP = new Set(['wrap', 'nowrap', 'balance', 'pretty']);
const TEXT_OVERFLOW = new Set(['ellipsis', 'clip']);
const BORDER_STYLES = new Set(['solid', 'dashed', 'dotted', 'double', 'hidden', 'none']);
const BORDER_SIDES = new Set(['x', 'y', 't', 'r', 'b', 'l', 's', 'e']);
const ROUNDED_SIDES = new Set([
  't',
  'r',
  'b',
  'l',
  's',
  'e',
  'tl',
  'tr',
  'br',
  'bl',
  'ss',
  'se',
  'ee',
  'es',
]);
const SHADOW_SIZES = new Set(['sm', 'md', 'lg', 'xl', '2xl', 'inner', 'none']);
const BG_SIZE = new Set(['auto', 'cover', 'contain']);
const BG_REPEAT = new Set([
  'repeat',
  'no-repeat',
  'repeat-x',
  'repeat-y',
  'repeat-round',
  'repeat-space',
]);
const BG_ATTACHMENT = new Set(['fixed', 'local', 'scroll']);
const BG_POSITION = new Set([
  'bottom',
  'center',
  'left',
  'left-bottom',
  'left-top',
  'right',
  'right-bottom',
  'right-top',
  'top',
]);
const FLEX_DIRECTION = new Set(['row', 'row-reverse', 'col', 'col-reverse']);
const FLEX_WRAP = new Set(['wrap', 'wrap-reverse', 'nowrap']);

function tokenize(value: string | null | undefined): string[] {
  if (!value) return [];
  return value.split(/\s+/).filter(Boolean);
}

/** Split on a separator, ignoring separators inside `[]` or `()`. */
function splitTopLevel(input: string, separator: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let current = '';

  for (const char of input) {
    if (char === '[' || char === '(') depth += 1;
    else if (char === ']' || char === ')') depth = Math.max(0, depth - 1);

    if (char === separator && depth === 0) {
      parts.push(current);
      current = '';
      continue;
    }
    current += char;
  }

  parts.push(current);
  return parts;
}

function stripOpacityModifier(utility: string): string {
  const parts = splitTopLevel(utility, '/');
  return parts.length > 1 ? parts.slice(0, -1).join('/') : utility;
}

function isLengthLike(value: string): boolean {
  if (value.startsWith('length:')) return true;
  if (value.startsWith('calc(')) return true;
  return /^-?\d*\.?\d+(px|rem|em|%|vh|vw|vmin|vmax|ch|ex|pt|pc|cm|mm|in)?$/.test(value);
}

function isWidthValue(value: string): boolean {
  if (/^\d+(\.\d+)?$/.test(value)) return true;
  if (value.startsWith('[') && value.endsWith(']')) return isLengthLike(value.slice(1, -1));
  return false;
}

function backgroundGroup(rest: string): string {
  if (rest === 'none') return 'bg-image';
  if (/^(gradient|linear|radial|conic)(-|$)/.test(rest)) return 'bg-image';
  if (rest.startsWith('[url(')) return 'bg-image';
  if (rest.startsWith('clip-')) return 'bg-clip';
  if (rest.startsWith('origin-')) return 'bg-origin';
  if (rest.startsWith('blend-')) return 'bg-blend';
  if (BG_SIZE.has(rest)) return 'bg-size';
  if (BG_REPEAT.has(rest)) return 'bg-repeat';
  if (BG_ATTACHMENT.has(rest)) return 'bg-attachment';
  if (BG_POSITION.has(rest)) return 'bg-position';
  return 'bg-color';
}

function textGroup(rest: string): string {
  if (TEXT_ALIGN.has(rest)) return 'text-align';
  if (TEXT_WRAP.has(rest)) return 'text-wrap';
  if (TEXT_OVERFLOW.has(rest)) return 'text-overflow';
  if (FONT_SIZES.has(rest)) return 'font-size';
  if (rest.startsWith('[') && rest.endsWith(']')) {
    return isLengthLike(rest.slice(1, -1)) ? 'font-size' : 'text-color';
  }
  return 'text-color';
}

function borderGroup(rest: string): string {
  if (rest === 'collapse' || rest === 'separate') return 'border-collapse';
  if (rest.startsWith('spacing-')) return 'border-spacing';
  if (BORDER_STYLES.has(rest)) return 'border-style';

  const segments = rest.split('-');
  const head = segments[0] ?? '';
  if (BORDER_SIDES.has(head)) {
    const value = segments.slice(1).join('-');
    if (value === '' || isWidthValue(value)) return `border-w-${head}`;
    return `border-color-${head}`;
  }

  return isWidthValue(rest) ? 'border-w' : 'border-color';
}

function roundedGroup(rest: string): string {
  const head = rest.split('-')[0] ?? '';
  return ROUNDED_SIDES.has(head) ? `rounded-${head}` : 'rounded';
}

function shadowGroup(rest: string): string {
  if (SHADOW_SIZES.has(rest)) return 'shadow';
  if (rest.startsWith('[')) return 'shadow';
  return 'shadow-color';
}

function flexGroup(rest: string): string {
  if (FLEX_DIRECTION.has(rest)) return 'flex-direction';
  if (FLEX_WRAP.has(rest)) return 'flex-wrap';
  return 'flex';
}

function fontGroup(rest: string): string {
  return FONT_WEIGHTS.has(rest) ? 'font-weight' : 'font-family';
}

/** Resolve the conflict group for a single utility (variants already removed). */
function classGroup(rawUtility: string): string | null {
  let utility = rawUtility;
  if (utility.startsWith('!')) utility = utility.slice(1);
  if (utility.startsWith('-')) utility = utility.slice(1);
  utility = stripOpacityModifier(utility);

  if (!utility) return null;
  // Arbitrary properties such as `[mask-type:luminance]` are left untouched.
  if (utility.startsWith('[')) return null;

  const exact = EXACT_GROUPS[utility];
  if (exact) return exact;

  if (utility.startsWith('bg-')) return backgroundGroup(utility.slice(3));
  if (utility.startsWith('text-')) return textGroup(utility.slice(5));
  if (utility.startsWith('border-')) return borderGroup(utility.slice(7));
  if (utility.startsWith('rounded-')) return roundedGroup(utility.slice(8));
  if (utility.startsWith('shadow-')) return shadowGroup(utility.slice(7));
  if (utility.startsWith('flex-')) return flexGroup(utility.slice(5));
  if (utility.startsWith('font-')) return fontGroup(utility.slice(5));
  if (utility.startsWith('from-')) return 'gradient-from';
  if (utility.startsWith('via-')) return 'gradient-via';
  if (utility.startsWith('to-')) return 'gradient-to';

  for (const [prefix, group] of PREFIX_GROUPS) {
    if (utility.startsWith(prefix)) return group;
  }

  return null;
}

function parseToken(token: string): ParsedToken {
  const segments = splitTopLevel(token, ':');
  const utility = segments.pop() ?? '';
  const variantKey = [...segments].sort().join(':');
  return { token, variantKey, group: classGroup(utility) };
}

function dominates(overrideGroup: string, baseGroup: string): boolean {
  if (overrideGroup === baseGroup) return true;
  return GROUP_MEMBERS[overrideGroup]?.includes(baseGroup) ?? false;
}

/**
 * Merge a preset (base) class string with a caller override so that the
 * override reliably wins for conflicting Tailwind utilities.
 *
 * @param base Preset supplied classes. Conflicting entries are dropped.
 * @param override Caller supplied classes. Returned unchanged, always last.
 */
export function mergePresetClasses(
  base?: string | null,
  override?: string | null,
): string {
  const baseTokens = tokenize(base);
  const overrideTokens = tokenize(override);

  if (baseTokens.length === 0) return overrideTokens.join(' ');
  if (overrideTokens.length === 0) return baseTokens.join(' ');

  const overrideEntries = overrideTokens
    .map(parseToken)
    .filter((entry): entry is ParsedToken & { group: string } => entry.group !== null);

  const keptBase = baseTokens.filter((token) => {
    const parsed = parseToken(token);
    if (!parsed.group) return true;
    return !overrideEntries.some(
      (entry) => entry.variantKey === parsed.variantKey && dominates(entry.group, parsed.group as string),
    );
  });

  return [...keptBase, ...overrideTokens].join(' ');
}

export default mergePresetClasses;